import { reportStartBuilding } from '../state/trial';
import { funnelStep } from './funnel';
import { secondsBucket } from './visits';

/** The part of the build store (state/buildRun.ts) the funnel reads: zustand's subscribe. */
interface WatchableBuild {
  subscribe?: (
    listener: (now: { status: string; heldApp?: unknown }, before: { status: string }) => void,
  ) => () => void;
}

/**
 * build_started, build_succeeded and build_failed, read off the build store's own transitions
 * rather than written into it. The store is the browser's longest and most careful piece of code
 * (idempotency keys, recovery, replays); three measurement calls threaded through it would be
 * three more things to keep right in every branch, while its status already says each moment:
 *
 *   - anything → 'running': `start()`, a Try again included. Only `start()` sets it.
 *   - 'running' → 'idle': it landed — the editor opened, or the pill offers it.
 *   - 'running' → 'error': it failed. recover() reporting a build a closed page never heard the
 *     end of goes idle → 'error', which is not counted: that build's start was another page's.
 *
 * Returns the unsubscribe. A store that cannot be watched (a test's stand-in) measures nothing.
 */
export function watchBuildRun(store: WatchableBuild): () => void {
  if (typeof store?.subscribe !== 'function') return () => undefined;
  // When the running build began, for `seconds` on its end — and whether one is running at all.
  let startedAt: number | null = null;
  const took = () => secondsBucket(Date.now() - (startedAt ?? Date.now()));
  const unsubscribe = store.subscribe((now, before) => {
    if (now.status === before.status) return;
    if (now.status === 'running') {
      startedAt = Date.now();
      funnelStep('build_started');
      // Meta's own event for it, once per visitor (handoff §4).
      reportStartBuilding();
    } else if (before.status === 'running' && now.status === 'idle') {
      // `held`: built before the card, waiting on the server for the trial (state/buildRun.ts).
      funnelStep('build_succeeded', { seconds: took(), ...(now.heldApp ? { held: true as const } : {}) });
      startedAt = null;
    } else if (before.status === 'running' && now.status === 'error') {
      funnelStep('build_failed', { seconds: took() });
      startedAt = null;
    }
  });
  /*
   * The page closed with a build running: the server may still finish it (a key replays it), but
   * the person did not wait to see it, which is the journey's question.
   */
  const onHide = (): void => {
    if (startedAt !== null) funnelStep('build_abandoned', { seconds: took() }, { now: true });
  };
  window.addEventListener('pagehide', onHide);
  return () => {
    unsubscribe();
    window.removeEventListener('pagehide', onHide);
  };
}
